import { AppRoute } from '@/types/route';
import { MenuItem, NavigationRules } from '@/module/configuration/navigation/types/navigation';
import { availableComponents } from './available-components';
import { default as rules } from '@/middlewares/check-rules';
import staticRoutes from './routes';
import VueRouter from 'vue-router';
import CustomView from '@/module/configuration/navigation/views/CustomView.vue';

class DynamicRoutesManager {
  private static instance: DynamicRoutesManager;
  private dynamicRoutes: AppRoute[] = [];
  private router: any;
  private currentDynamicRouteNames: string[] = []; // Agregar para tracking de rutas dinámicas

  private constructor() {}

  static getInstance(): DynamicRoutesManager {
    if (!DynamicRoutesManager.instance) {
      DynamicRoutesManager.instance = new DynamicRoutesManager();
    }
    return DynamicRoutesManager.instance;
  }

  setRouter(router: any) {
    this.router = router;
  }

  generateRouteFromMenuItem(menuItem: MenuItem): AppRoute | null {
    if (!menuItem.module || !availableComponents[menuItem.module]) {
      return null;
    }

    const componentConfig = availableComponents[menuItem.module];
    const baseName = this.formatNameToCamelCase(menuItem.language_key?.[0]?.label || menuItem.module);

    // Buscar la ruta base correspondiente en las rutas estáticas
    const baseRoute = staticRoutes.find(route => 
      route.meta?.domain === componentConfig.domain &&
      route.meta?.isBaseRoute
    );
    
    // Construir nombre y path únicos pero predecibles
    const routeName = `${menuItem.module.toLowerCase()}-${baseName}`;
    const routePath = `/custom/${menuItem.module.toLowerCase()}/${baseName}`.toLowerCase();
    
    return {
      path: routePath,
      name: routeName,
      component: componentConfig.component,
      beforeEnter: baseRoute?.beforeEnter || rules.navigate,
      meta: {
        domain: componentConfig.domain,
        menuKey: menuItem.menuKey,
        isCustomRoute: true,
        originalName: menuItem.name,
        module: menuItem.module,
        parentRoute: baseRoute?.name
      },
      props: route => ({
        menuKey: menuItem.menuKey,
        customConfig: menuItem
      })
    };
  }

  private formatNameToCamelCase(name: string): string {
    return name
      .replace(/[^a-zA-Z0-9 ]/g, '') // Remover caracteres especiales
      .split(' ')
      .map((word, index) => {
        if (index === 0) {
          return word.toLowerCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join('');
  }

  clearDynamicRoutes(): void {
    if (!this.router) return;

    // En Vue Router 3, necesitamos recrear el router con las rutas actualizadas
    const remainingRoutes = this.router.options.routes.filter(route => 
      !this.currentDynamicRouteNames.includes(route.name)
    );

    // Crear un nuevo router con las rutas restantes
    const newRouter = new VueRouter({
      ...this.router.options,
      routes: remainingRoutes
    });

    // Reemplazar el matcher del router actual
    // @ts-ignore - El tipo de Vue Router no incluye _matcher pero existe
    this.router.matcher = newRouter.matcher;

    // Limpiar el tracking
    this.currentDynamicRouteNames = [];
    this.dynamicRoutes = [];
  }

  updateRoutesFromNavigation(navigationRules: NavigationRules): void {
    this.clearDynamicRoutes();

    Object.entries(navigationRules).forEach(([key, item]) => {
      if (item.path) {
        const routeName = item.name || `dynamic-${key}`;
        const route = {
          path: item.path,
          name: routeName,
          component: CustomView,
          meta: {
            ...item.meta,
            menuKey: key,
            permissionRequired: item.permissionRequired
          }
        };

        // Agregar la ruta dinámica
        this.router.addRoute(route);
        this.currentDynamicRouteNames.push(routeName);
      }

      // Manejar submenús si existen
      if (item.subMenu) {
        Object.entries(item.subMenu).forEach(([subKey, subItem]) => {
          if (subItem.path) {
            const subRouteName = subItem.name || `dynamic-${key}-${subKey}`;
            const subRoute = {
              path: subItem.path,
              name: subRouteName,
              component: CustomView,
              meta: {
                ...subItem.meta,
                menuKey: `${key}-${subKey}`,
                permissionRequired: subItem.permissionRequired
              }
            };

            this.router.addRoute(subRoute);
            this.currentDynamicRouteNames.push(subRouteName);
          }
        });
      }
    });
  }

  getDynamicRoutes(): AppRoute[] {
    return this.dynamicRoutes;
  }
}

export const dynamicRoutesManager = DynamicRoutesManager.getInstance();
